import { useEffect, useRef, useState } from 'react';
import fm from 'format-message';
import PropTypes from 'prop-types';
import headerThemes from 'config/branding/header-themes';
import useHeaderNavigationQuery from 'queries/useHeaderNavigationQuery';
import Link from '@activebrands/core-web/components/Link';
import AnimatedOverlay from '@activebrands/core-web/components/overlays/AnimatedOverlay';
import media from '@activebrands/core-web/config/media';
import { OVERLAY_ID } from '@activebrands/core-web/config/overlay';
import { StateProvider } from '@activebrands/core-web/contexts/StateContext';
import { useDidUpdate } from '@activebrands/core-web/hooks/lifecycle';
import useEventListener from '@activebrands/core-web/hooks/useEventListener';
import useEvent from '@activebrands/core-web/libs/Events/useEvent';
import overlay, { useOverlaySubscriber } from '@activebrands/core-web/libs/overlay';
import { isLocked as isScrollLocked } from '@activebrands/core-web/libs/scroll';
import { styled } from '@activebrands/core-web/libs/styletron';
import { isIOS } from '@activebrands/core-web/utils/constants';
import { matchesMedia } from '@activebrands/core-web/utils/responsive';
import { getFocusRingStyles } from '@activebrands/core-web/utils/style-functions/get-focus-styles';
import debounce from '@grebban/utils/function/debounce';
import loadable from '@loadable/component';
import { useLocation } from '@reach/router';
import AuthLink from 'components/Header/AuthLink';
import Logotype from 'components/Header/Logotype';
import Navigation from 'components/Header/Navigation';
import PrimaryLevelOneDesktop from 'components/Header/Navigation/PrimaryMenu/PrimaryLevelOneDesktop';
import SecondaryLevelOneDesktop from 'components/Header/Navigation/SecondaryMenu/SecondaryLevelOneDesktop';
import OverlayToggler from 'components/Header/OverlayToggler';
import SearchInput from 'components/Header/SearchInput';
import BasketCounterIcon from 'components/icons/BasketCounterIcon';
import CloseIcon from 'components/icons/CloseIcon';
import MenuIcon from 'components/icons/MenuIcon';
import SearchIcon from 'components/icons/SearchIcon';
import StateIcon from 'components/icons/StateIcon';
import Paragraph from 'components/text/Paragraph';
import { useStyletron } from 'styletron-react';

const SearchOverlay = loadable(() => import('components/Header/SearchOverlay'));
const BasketOverlay = loadable(() => import('components/Header/BasketOverlay'));

const HeaderElement = styled('header', {
    alignItems: 'center',
    backgroundColor: 'var(--color-bg-header)',
    borderRadius: '0 0 16px 16px ',
    top: 0,
    color: 'var(--color-text-header)',
    display: 'flex',
    height: 'var(--height-header)',
    justifyContent: 'space-between',
    paddingLeft: 'var(--margin-content)',
    paddingRight: 'var(--margin-content)',
    position: 'fixed',
    transition: 'color, background-color var(--duration-fast) var(--ease)',
    width: 'calc(100% - 8px)',
    zIndex: 'var(--zindex-header)',

    [media.min['tablet.sm']]: {
        ':hover': {
            backgroundColor: 'var(--color-bg-secondary)',
            color: 'var(--color-text)',
        },
    },
});

const MainHeaderNav = styled('nav', {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
});

const IconWrapper = styled('div', {
    display: 'flex',
    gap: '8px',
    [media.min['desktop.sm']]: {
        gap: 'unset',
    },
});

const Header = ({
    alternateHrefs = [],
    isCheckout = false,
    backToSiteLabel = '',
    hasBanner = false,
    previewDataOverride,
    theme = 'default',
}) => {
    const headerRef = useRef(null);
    const [headerOverlayCloseEnabled, setHeaderOverlayCloseEnabled] = useState(false);
    const [headerSearchBarVisible, setHeaderSearchBarVisible] = useState(false);
    const listener = useEventListener('window');

    useEffect(() => {
        const sync = () => {
            setHeaderOverlayCloseEnabled(matchesMedia(media.max['mobile.sm']));
            setHeaderSearchBarVisible(matchesMedia(media.min['tablet.sm']));
        };
        sync();

        const debouncedSync = debounce(sync, 250);
        const handler = listener.subscribe('resize', debouncedSync);

        return () => {
            debouncedSync.cancel();
            handler && listener.unsubscribe('resize', handler);
        };
    }, []);

    const searchSkipLinkTargetId = headerSearchBarVisible ? 'search-header' : undefined;
    const location = useLocation();
    const [css] = useStyletron();
    const headerConfigContent = useHeaderNavigationQuery();
    const pageData = previewDataOverride || headerConfigContent;
    const { primaryMenu, secondaryMenu = [] } = pageData;
    const [menuValue, setMenuValue] = useState({ current: null, prev: null });

    const openOverlays = useOverlaySubscriber();

    const headerOverlays = [
        OVERLAY_ID.ACCOUNT_NAVIGATION,
        OVERLAY_ID.BASKET,
        OVERLAY_ID.CATEGORY_FACET_GROUPS,
        OVERLAY_ID.CATEGORY_SORT_OPTIONS,
        OVERLAY_ID.NAVIGATION,
        OVERLAY_ID.QUICKSHOP,
        OVERLAY_ID.SEARCH_FACET_GROUPS,
        OVERLAY_ID.SEARCH_SORT_OPTIONS,
        OVERLAY_ID.SEARCH,
        OVERLAY_ID.SHOP_THE_LOOK,
        OVERLAY_ID.SIZEGUIDE,
        OVERLAY_ID.SITE_SELECTOR,
        OVERLAY_ID.UNAUTHENTICATED,
        OVERLAY_ID.WRONG_SITE_OVERLAY,
    ];

    const headerOverlayIsOpen = headerOverlays.includes(openOverlays[0]);
    const navigationOverlayIsOpen = openOverlays[0] === OVERLAY_ID.NAVIGATION;
    const searchOverlayIsOpen = openOverlays[0] === OVERLAY_ID.SEARCH;
    const basketOverlayIsOpen = openOverlays[0] === OVERLAY_ID.BASKET;
    const menuOverlayIsOpen = headerOverlayCloseEnabled ? headerOverlayIsOpen : navigationOverlayIsOpen;
    const menuOverlayId = headerOverlayCloseEnabled && headerOverlayIsOpen ? openOverlays[0] : OVERLAY_ID.NAVIGATION;

    let isDefaultTheme = false;

    //These are only used by scroll
    const setDefaultStyleProperties = () => {
        isDefaultTheme = true;
        headerRef.current.style.setProperty('--color-bg-header', 'var(--color-bg-secondary)');
        headerRef.current.style.setProperty('--color-search-header', 'var(--color-bg-secondary)');
        headerRef.current.style.setProperty('--color-text-search-header', 'var(--color-text)');
        headerRef.current.style.setProperty('--color-text-header', 'var(--color-text)');
    };

    const resetHeaderStyleProperties = () => {
        isDefaultTheme = false;
        headerRef.current.style.removeProperty('--color-bg-header');
        headerRef.current.style.removeProperty('--color-search-header');
        headerRef.current.style.removeProperty('--color-text-search-header');
        headerRef.current.style.removeProperty('--color-text-header');
    };

    const resetNavigation = () => {
        const navigation = document.getElementById('navigation');
        navigation.scrollTop = 0;
        setMenuValue({ current: null, prev: null });
        overlay.closeAll();
    };

    useDidUpdate(() => {
        resetHeaderStyleProperties();
    }, [location.pathname]);

    useEvent('OVERLAY.STATE_CHANGE', ({ type, ids }) => {
        const someHeaderOverlayIsOpen = type === 'open' && headerOverlays.includes(ids[0]);
        const scrollYPosition = document.documentElement.scrollTop;

        if (ids.length === 0) {
            return;
        }

        if (someHeaderOverlayIsOpen) {
            setDefaultStyleProperties();
        } else if (scrollYPosition === 0) {
            resetHeaderStyleProperties();
        }
    });

    useEvent(
        'BODY.SCROLL_DOWN',
        position => {
            const scrollDownThreshold = position.y > 0;
            if (scrollDownThreshold && !isDefaultTheme) {
                setDefaultStyleProperties();
            }
        },
        [hasBanner]
    );

    useEvent(
        'BODY.SCROLL_UP',
        position => {
            const scrollUpThreshold = position.y > 0;
            if (isIOS && isScrollLocked()) {
                return;
            }

            if (!scrollUpThreshold && isDefaultTheme) {
                resetHeaderStyleProperties();
            }
        },
        [hasBanner]
    );

    return (
        <StateProvider key="SearchProvider">
            <div className={css({ display: 'flex', justifyContent: 'center' })}>
                <HeaderElement
                    $style={headerThemes[isCheckout ? 'checkout' : theme]}
                    data-selector={isCheckout ? 'checkout.nav' : 'global.nav'}
                    ref={headerRef}
                >
                    {isCheckout ? (
                        <Link
                            className={css({
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                ...getFocusRingStyles(),
                            })}
                            data-selector="checkout.nav.continue-shopping"
                            to="/"
                        >
                            <StateIcon size="16px" state="arrow-left" />
                            <Paragraph fontKeys="Miscellaneous/14_100">{backToSiteLabel}</Paragraph>
                        </Link>
                    ) : (
                        <MainHeaderNav id="primary-nav" aria-label={fm('Main menu')}>
                            <div
                                className={css({
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                })}
                            >
                                <OverlayToggler
                                    data-selector="global.nav.menu-toggle"
                                    aria-label={menuOverlayIsOpen ? fm('Close') : fm('Open menu')}
                                    isOpen={menuOverlayIsOpen}
                                    overlayId={menuOverlayId}
                                    resetNavigation={resetNavigation}
                                >
                                    {isOpen =>
                                        isOpen ? (
                                            <CloseIcon
                                                $style={{
                                                    width: ['24px', null, null, null, null, '16px'],
                                                    height: ['24px', null, null, null, null, '16px'],
                                                }}
                                            />
                                        ) : (
                                            <MenuIcon
                                                $style={{
                                                    width: ['24px', null, null, null, null, '16px'],
                                                    height: ['24px', null, null, null, null, '16px'],
                                                }}
                                            />
                                        )
                                    }
                                </OverlayToggler>
                                <PrimaryLevelOneDesktop
                                    items={primaryMenu}
                                    menuValue={menuValue}
                                    resetNavigation={resetNavigation}
                                    setMenuValue={setMenuValue}
                                />
                                <Navigation
                                    alternateHrefs={alternateHrefs}
                                    isOpen={navigationOverlayIsOpen}
                                    menuValue={menuValue}
                                    pageData={pageData}
                                    resetNavigation={resetNavigation}
                                    setMenuValue={setMenuValue}
                                />
                                <OverlayToggler
                                    data-selector="global.nav.search"
                                    id={headerSearchBarVisible ? undefined : 'search-header'}
                                    $style={{ display: ['block', null, null, null, null, 'none'], marginRight: 'auto' }}
                                    aria-label={searchOverlayIsOpen ? fm('Close search') : fm('Open search')}
                                    overlayId={OVERLAY_ID.SEARCH}
                                    isOpen={searchOverlayIsOpen}
                                    resetNavigation={resetNavigation}
                                >
                                    <SearchIcon
                                        $style={{
                                            display: 'block',
                                            width: ['22px', null, null, null, null, '16px'],
                                            height: ['22px', null, null, null, null, '16px'],
                                        }}
                                    />
                                </OverlayToggler>
                            </div>
                            <SecondaryLevelOneDesktop
                                items={secondaryMenu}
                                menuValue={menuValue}
                                resetNavigation={resetNavigation}
                                setMenuValue={setMenuValue}
                            />
                        </MainHeaderNav>
                    )}
                    <Logotype
                        $style={{ display: ['none', null, null, null, null, 'inline-flex'] }}
                        data-selector={isCheckout ? 'checkout.nav.logo' : 'global.nav.logo'}
                    />
                    {!isCheckout ? (
                        <Logotype
                            $style={{ display: ['inline-flex', null, null, null, null, 'none'] }}
                            data-selector="global.nav.logo"
                            width="80px"
                        />
                    ) : null}
                    {isCheckout && (
                        <>
                            <Paragraph
                                $style={{
                                    display: ['none', null, null, null, null, 'flex'],
                                    color: 'var(--color-text-subtle)',
                                }}
                                data-selector="checkout.nav.title"
                                fontKeys="Miscellaneous/14_100"
                            >
                                {fm('Checkout')}
                            </Paragraph>
                            <Logotype
                                $style={{
                                    display: ['inline-flex', null, null, null, null, 'none'],
                                    right: 0,
                                    left: 'unset',
                                    transform: 'translate(0%, -45%)',
                                    paddingRight: 'var(--margin-content)',
                                }}
                                data-selector="checkout.nav.logo"
                                width={['56px', '80px']}
                            />
                        </>
                    )}
                    {!isCheckout && (
                        <div className={css({ display: 'flex', justifyContent: 'center', alignItems: 'center' })}>
                            <SearchInput
                                role="search"
                                skipLinkTargetId={searchSkipLinkTargetId}
                                $style={{
                                    display: ['none', null, null, null, null, 'flex'],
                                    margin: '0 16px',
                                }}
                                previewDataOverride={previewDataOverride}
                            />
                            <IconWrapper>
                                <AuthLink data-selector="global.nav.account" />
                                <OverlayToggler
                                    aria-label={
                                        basketOverlayIsOpen ? fm('Close shopping bag') : fm('Open shopping bag')
                                    }
                                    data-selector="global.nav.cart"
                                    isOpen={basketOverlayIsOpen}
                                    overlayId={OVERLAY_ID.BASKET}
                                    resetNavigation={resetNavigation}
                                >
                                    <BasketCounterIcon />
                                </OverlayToggler>
                            </IconWrapper>
                        </div>
                    )}
                </HeaderElement>
            </div>
            <AnimatedOverlay
                lockScroll
                $style={{
                    bottom: '0',
                    height: 'calc(100dvh - var(--height-header))',
                }}
                component={BasketOverlay}
                id={OVERLAY_ID.BASKET}
            />
            <AnimatedOverlay
                lockScroll
                $style={{
                    bottom: '0',
                    height: 'calc(100dvh - var(--height-header))',
                }}
                component={SearchOverlay}
                id={OVERLAY_ID.SEARCH}
            />
        </StateProvider>
    );
};

Header.propTypes = {
    alternateHrefs: PropTypes.array,
    backToSiteLabel: PropTypes.string,
    hasBanner: PropTypes.bool,
    isCheckout: PropTypes.bool,
    previewDataOverride: PropTypes.object,
    theme: PropTypes.string.isRequired,
};

export default Header;
